import React, {
  ComponentProps,
  forwardRef,
  memo,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { css, useTheme } from '@emotion/react'
import styled from '@emotion/styled'

import { SvgIcon } from '@surglogs-app/icons'
import { PropsWithTheme } from '@surglogs-app/ui.theme'

import { TextLine } from '../../components/TextLine'
import { absoluteVerticalCenter } from '../../mixins'
import {
  getBorderColor,
  getLeftPadding,
  getOutlineColor,
  getRightPadding,
  getSizeStyles,
} from '../services/InputService'
import {
  getCSSObjectFromStyleProps,
  StyleProps,
} from '../styleProps/StylePropsService'

import { Box } from './Box'
import { FlexBox } from './FlexBox'
import { Text } from './Text'

export type InputSize = 'default' | 'mini'

export interface InputStyleProps extends StyleProps {
  warning?: boolean
  invalid?: boolean
  disabled?: boolean
  inputSize?: InputSize
  withIcon?: boolean
  adornmentWidth?: number
}

export const inputStyles = ({
  theme,
  disabled,
  ...rest
}: PropsWithTheme<InputStyleProps>) => css`
  box-sizing: border-box;
  display: inline-block;
  text-overflow: ellipsis;
  width: 100%;
  position: relative;
  appearance: none;
  outline: none;
  font-family: ${theme.ui.typography.fontFamilies.default};
  font-weight: ${theme.ui.typography.fontWeights.regular};

  ${getSizeStyles({ theme, ...rest })}

  &, // set input and icon color
  & + svg {
    color: ${theme.ui.colors['navy.100']};
  }

  border: 1px solid;
  border-color: ${theme.ui.colors[getBorderColor({ disabled, ...rest })]};

  &:disabled {
    background-color: ${theme.ui.colors['navy.10']};
    border-color: ${theme.ui.colors['navy.10']};
    pointer-events: none;

    &, // set input and icon color
    & + svg {
      color: ${theme.ui.colors['navy.25']};
    }
  }

  &:focus,
  &:active,
  &:hover {
    border-color: ${theme.ui.colors[getOutlineColor({ disabled, ...rest })]};
  }

  // Do we still need this ?
  &::-ms-clear {
    width: 0;
    height: 0;
  }

  &::placeholder {
    color: ${theme.ui.colors['navy.25']};
  }

  &:read-only {
    cursor: default;
  }
`

const Adornment = styled(FlexBox)`
  flex: 1;
  align-items: center;

  ${absoluteVerticalCenter()}
  right: ${({ theme }) => theme.ui.spacings.md};
`

const StyledInput = styled.input<InputStyleProps>`
  & + svg {
    // height instead of width because of correct sizing when viewbox is smaller than 24px
    height: calc(
      ${({ theme, inputSize }) =>
          theme.ui.spacings[inputSize === 'mini' ? 'xl' : 'xxl']} / 2
    );
    flex-shrink: 0;

    ${absoluteVerticalCenter()}
    left: ${({ theme, inputSize }) =>
      theme.ui.spacings[inputSize === 'mini' ? 'sm' : 'md']};
  }

  ${inputStyles}
  ${getLeftPadding}
  ${getRightPadding}

  ${getCSSObjectFromStyleProps}
`

const DEFAULT_WIDTH = 50

export interface InputProps
  extends Omit<ComponentProps<typeof StyledInput>, 'size'>,
    Pick<InputStyleProps, 'warning' | 'disabled' | 'invalid'> {
  adornment?: ReactNode | null
  adornmentProps?: ComponentProps<typeof Adornment>
  wrapperProps?: ComponentProps<typeof Box>
  icon?: SvgIcon
  size?: InputSize
}

export const Input = memo(
  forwardRef<HTMLInputElement, InputProps>(
    (
      {
        disabled,
        warning,
        invalid,
        placeholder,
        size = 'default',
        adornment,
        adornmentProps,
        wrapperProps,
        icon: Icon,
        ...rest
      },
      ref,
    ) => {
      const [adornmentWidth, setAdornmentWidth] = useState<number | undefined>()
      const adornmentRef = useRef<HTMLDivElement>(null)
      const theme = useTheme()

      const adornmentContent = useMemo(() => {
        if (!adornment) {
          return null
        }

        if (typeof adornment === 'string') {
          return (
            <Adornment {...{ ref: adornmentRef, ...adornmentProps }}>
              <TextLine
                {...{
                  width: DEFAULT_WIDTH,
                  fadeOutColor: disabled
                    ? theme.ui.colors['navy.10']
                    : theme.ui.colors['white.100'],
                }}>
                <Text {...{ opacity: 0.25, display: 'inline-block' }}>
                  {adornment}
                </Text>
              </TextLine>
            </Adornment>
          )
        }

        return <Adornment {...{ ref: adornmentRef }}>{adornment}</Adornment>
      }, [adornment, adornmentProps, disabled, theme.ui.colors])

      useEffect(() => {
        if (adornmentRef.current) {
          setAdornmentWidth(adornmentRef.current.clientWidth)
        }
      }, [adornment])

      const component = useMemo(() => {
        return (
          <StyledInput
            {...rest}
            {...{
              ref,
              placeholder,
              'aria-invalid': rest['aria-invalid'] ?? invalid,
              invalid,
              warning,
              disabled,
              adornmentWidth,
              withIcon: !!Icon,
              inputSize: size,
            }}
          />
        )
      }, [
        Icon,
        adornmentWidth,
        disabled,
        invalid,
        placeholder,
        ref,
        rest,
        size,
        warning,
      ])

      // don't wrap unnecessarily into Box, to keep local stylings
      if (!adornment && !Icon) {
        return component
      }

      return (
        <Box {...{ position: 'relative', ...wrapperProps }}>
          {component}
          {Icon ? <Icon /> : null}
          {adornmentContent}
        </Box>
      )
    },
  ),
)
